import { Link } from "react-router-dom";
import { Crown, Star, MapPin, BadgeCheck } from "lucide-react";
import { API } from "../lib/api";

export function TeacherCard({ teacher }) {
  const photoUrl = teacher.photo_path ? `${API}/files/${teacher.photo_path}` : null;
  const verified = teacher.approval_status === "approved";
  return (
    <Link
      to={`/teacher/${teacher.id}`}
      className="bg-white rounded-3xl p-5 border border-black/5 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-200 block"
      data-testid={`teacher-card-${teacher.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={teacher.name}
              loading="lazy"
              decoding="async"
              className="h-14 w-14 rounded-full object-cover border border-black/10 select-none pointer-events-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              data-testid={`teacher-card-photo-${teacher.id}`}
            />
          ) : (
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center font-display font-bold text-xl"
              style={{ background: "#FFD708", color: "#13A699" }}
            >
              {(teacher.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          {verified && (
            <span
              title="Verified by Rate-ED"
              className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5 shadow-sm"
              data-testid={`teacher-card-verified-${teacher.id}`}
            >
              <BadgeCheck className="h-4 w-4" fill="#13A699" stroke="#FFF7ED" strokeWidth={2.5} />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-display font-bold text-lg truncate" style={{ color: "#13A699" }}>
              {teacher.name}
            </span>
            {verified && (
              <span
                className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ background: "#13A699", color: "#FFF7ED" }}
              >
                <BadgeCheck className="h-3 w-3" /> Verified
              </span>
            )}
            {teacher.verified_professional && (
              <span
                className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ background: "#3B82F6", color: "#FFFFFF" }}
                data-testid={`teacher-card-pro-${teacher.id}`}
                title="Verified Professional Mentor"
              >
                <BadgeCheck className="h-3 w-3" /> Pro Verified
              </span>
            )}
            {teacher.featured && (
              <span
                className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ background: "#FFD708", color: "#13A699" }}
              >
                <Crown className="h-3 w-3" /> Featured
              </span>
            )}
          </div>
          {teacher.location && (
            <div className="text-xs flex items-center gap-1" style={{ color: "#13A69999" }}>
              <MapPin className="h-3 w-3" /> {teacher.location}
            </div>
          )}
        </div>
      </div>
      <p className="mt-3 text-sm line-clamp-3" style={{ color: "#13A699" }}>
        {teacher.bio || "No bio yet."}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {(teacher.subjects || []).slice(0, 3).map((s) => (
          <span
            key={s}
            className="text-xs font-bold px-2 py-1 rounded-full"
            style={{ background: "#FFF7ED", color: "#13A699", border: "1px solid #13A69922" }}
          >
            {s}
          </span>
        ))}
      </div>
      {(teacher.teaching_locations || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`teacher-card-locations-${teacher.id}`}>
          {teacher.teaching_locations.map((loc) => (
            <span
              key={loc}
              className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
              style={{ background: "#FFD708", color: "#13A699" }}
            >
              <MapPin className="h-2.5 w-2.5" />
              {loc === "explorer_home" ? "Home visit" : "Mentor location"}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm font-bold" style={{ color: "#13A699" }}>
          ₹{teacher.fees_per_hour}/hr
        </div>
        <div className="text-sm font-bold inline-flex items-center gap-1" style={{ color: "#13A699" }}>
          <Star className="h-3 w-3" fill={teacher.rating_avg ? "#FFD708" : "transparent"} stroke="#13A699" />
          {teacher.rating_avg ? teacher.rating_avg.toFixed(1) : "—"} ({teacher.rating_count || 0})
        </div>
      </div>
      <div className="mt-2 text-xs" style={{ color: "#13A69999" }}>
        {teacher.experience_years || 0} yrs experience
      </div>
    </Link>
  );
}

export function TeacherCardSkeleton() {
  return (
    <div
      className="bg-white rounded-3xl p-5 border border-black/5 shadow-sm animate-pulse"
      aria-hidden="true"
      data-testid="teacher-card-skeleton"
    >
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 rounded-full bg-[#FFF7ED]" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 rounded bg-[#FFF7ED]" />
          <div className="h-3 w-20 rounded bg-[#FFF7ED]" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <div className="h-3 w-full rounded bg-[#FFF7ED]" />
        <div className="h-3 w-5/6 rounded bg-[#FFF7ED]" />
      </div>
      <div className="mt-4 flex gap-2">
        <div className="h-5 w-14 rounded-full bg-[#FFF7ED]" />
        <div className="h-5 w-16 rounded-full bg-[#FFF7ED]" />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="h-4 w-12 rounded bg-[#FFF7ED]" />
        <div className="h-4 w-16 rounded bg-[#FFF7ED]" />
      </div>
    </div>
  );
}
